import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { onContractService } from "../../services/onContractService"
import reportService from "../../services/ReportService"
import { onContractPaymentService } from "../../services/onContractPaymentService"
import { shipmentService } from "../../services/shipmentService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js" 
import Alert from "../common/Alert"
import AddPaymentModal from "./on-contract-payment/AddPaymentModal"
import EditPaymentModal from "./on-contract-payment/EditPaymentModal"
import ViewPaymentModal from "./on-contract-payment/ViewPaymentModal"
import {
  ArrowLeft, Pencil, Download, TrendingUp, TrendingDown,
  FileText, Truck, Plus, Trash2, IndianRupee, Wallet,
} from "lucide-react"
import Button from '../common/Button.js'
import RowActionsMenu from '../common/RowActionsMenu.js'
import { useTranslation } from 'react-i18next';
import { formatAuditUser } from '../../utils/userDisplay'


const ViewOnContract = () => {
  const { t } = useTranslation();
  const { contractId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [contract, setContract] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [expandedML, setExpandedML] = useState(null)

  const handleToggleML = async (mlId, detail) => {
    if (expandedML === mlId) {
      setExpandedML(null)
      return
    }
    setExpandedML(mlId)
    // If already fetched, skip
    if (mlShipments[mlId]) return
    if (!detail?.shipment_ids?.length) return
    try {
      setMlShipmentsLoading((prev) => ({ ...prev, [mlId]: true }))
      const results = await Promise.all(
        detail.shipment_ids.map((sId) =>
          shipmentService.getShipment(sId).then((r) => r.data.shipment).catch(() => null)
        )
      )
      setMlShipments((prev) => ({ ...prev, [mlId]: results.filter(Boolean) }))
    } catch (err) {
      console.error("Failed to load shipments for ML", mlId, err)
    } finally {
      setMlShipmentsLoading((prev) => ({ ...prev, [mlId]: false }))
    }
  }

  // Payment state
  const [payments, setPayments] = useState([])
  const [paidAmount, setPaidAmount] = useState(0)
  const [balanceAmount, setBalanceAmount] = useState(0)
  const [paymentsLoading, setPaymentsLoading] = useState(false)
  const [showAddPayment, setShowAddPayment] = useState(false)
  const [editPayment, setEditPayment] = useState(null)
  const [viewPayment, setViewPayment] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [openDropdown, setOpenDropdown] = useState(null)
  const [mlShipments, setMlShipments] = useState({})
  const [mlShipmentsLoading, setMlShipmentsLoading] = useState({})

  useEffect(() => {
    fetchContract()
    fetchPayments()
  }, [contractId])

  const fetchContract = async () => {
    try {
      setLoading(true)
      const res = await onContractService.getOne(contractId)
      setContract(res.data.contract)
      // Sync balance from contract (authoritative source)
      if (res.data.contract?.balance_amount !== undefined) {
        setBalanceAmount(res.data.contract.balance_amount)
      }
      if (res.data.contract?.paid_amount !== undefined) {
        setPaidAmount(res.data.contract.paid_amount)
      }
    } catch (err) {
      console.error("Error loading contract:", err)
      Alert.error("Failed to load contract")
      navigate("/on-contracts")
    } finally {
      setLoading(false)
    }
  }

  const fetchPayments = async () => {
    try {
      setPaymentsLoading(true)
      const res = await onContractPaymentService.getAll(contractId)
      setPayments(res.data.payments || [])
      setPaidAmount(res.data.paid_amount ?? 0)
      // balance_amount comes from the contract object, not the payments API
      // Only set from payments API if explicitly returned, otherwise fall back to contract
      if (res.data.balance_amount !== undefined) {
        setBalanceAmount(res.data.balance_amount)
      }
    } catch (err) {
      console.error("Error loading payments:", err)
    } finally {
      setPaymentsLoading(false)
    }
  }

  const handlePaymentSuccess = () => {
    fetchPayments()
    fetchContract()
  }

  const handleDeletePayment = async (payment) => {
    if (!window.confirm(`Delete payment ${payment.payment_id} of ₹${fmt(payment.amount)}? This cannot be undone.`))
      return
    try {
      setDeletingId(payment.payment_id)
      await onContractPaymentService.delete(contractId, payment.payment_id)
      Alert.success("Payment deleted")
      handlePaymentSuccess()
    } catch (err) {
      Alert.error(err.response?.data?.message || "Failed to delete payment")
    } finally {
      setDeletingId(null)
    }
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      await reportService.downloadOnContractReport({ contract_id: contractId })
      Alert.success("Excel report downloaded")
    } catch (err) {
      Alert.error("Failed to export report")
    } finally {
      setExporting(false)
    }
  }

  const canEdit = () =>
    user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant"

  const fmt = (v) =>
    parseFloat(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading contract..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!contract) return null

  const pl = parseFloat(contract.trip_pl || 0)
  const plPositive = pl >= 0

  const StatCard = ({ label, value, sub, color = "text-secondary", bg = "bg-white" }) => (
    <div className={`${bg} rounded-xl shadow p-4 flex flex-col gap-1`}>
      <div className="text-xs text-gray-500 font-medium">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400">{sub}</div>}
    </div>
  )

  return (
    <DashboardLayout>

                {/* Page Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                  <div className="flex items-center gap-3">
                    <button onClick={() => navigate("/on-contracts")} className="text-gray-500 hover:text-gray-700">
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                      <h1 className="text-xl font-bold text-secondary">{contractId}</h1>
                      <p className="text-sm text-gray-500">
                        {contract.vehicle_number} · {contract.vehicle_name}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleExport}
                      disabled={exporting}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-green-700 bg-green-50 border border-green-300 rounded-xl hover:bg-green-100 font-semibold disabled:opacity-50"
                    >
                      <Download className="w-4 h-4" />
                      {exporting ? "Exporting..." : "Export Excel"}
                    </button>
                    {canEdit() && (
                      <Button
                        variant="primary"
                        icon={Pencil}
                        onClick={() => navigate(`/on-contracts/edit/${contractId}`)}
                      >
                        Edit
                      </Button>
                    )}
                  </div>
                </div>

                {/* Vehicle & Period Info */}
                <div className="bg-white rounded-xl shadow p-5 mb-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Vehicle Number</div>
                      <div className="font-bold text-secondary text-sm flex items-center gap-1">
                        <Truck className="w-3.5 h-3.5 text-blue-500" />
                        {contract.vehicle_number}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Vehicle Name</div>
                      <div className="font-semibold text-secondary text-sm">{contract.vehicle_name}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Start Date</div>
                      <div className="font-semibold text-secondary text-sm">{contract.start_date}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">End Date</div>
                      <div className="font-semibold text-secondary text-sm">{contract.end_date}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">{t('common.createdBy')}</div>
                      <div className="font-semibold text-secondary text-sm">{formatAuditUser(contract, 'created_by')}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Created At</div>
                      <div className="font-semibold text-secondary text-sm">
                        {contract.created_at ? new Date(contract.created_at).toLocaleDateString("en-IN") : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Last Updated</div>
                      <div className="font-semibold text-secondary text-sm">
                        {contract.updated_at ? new Date(contract.updated_at).toLocaleDateString("en-IN") : "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Updated By</div>
                      <div className="font-semibold text-secondary text-sm">
                        {contract.updated_by || contract.updated_by_display
                          ? formatAuditUser(contract, 'updated_by')
                          : "—"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                  <StatCard label="ML Sheets" value={contract.ml_sheet_ids?.length || 0} color="text-blue-600" />
                  <StatCard label="Total Shipments" value={contract.total_shipments} color="text-secondary" />
                  <StatCard
                    label="Freight"
                    value={`₹${fmt(contract.total_revenue)}`}
                    color="text-green-600"
                  />
                  <StatCard
                    label="Contract Value"
                    value={`₹${fmt(contract.contract_value)}`}
                    color="text-secondary"
                  />
                  <StatCard
                    label="Charges"
                    value={`₹${fmt(
                      parseFloat(contract.handling_charges || 0) +
                        parseFloat(contract.parking_charges || 0) +
                        parseFloat(contract.other_charges || 0)
                    )}`}
                    sub="Handling + Parking + Other"
                    color="text-secondary"
                  />
                  <div
                    className={`rounded-xl shadow p-4 flex flex-col gap-1 ${
                      plPositive ? "bg-green-50 border border-green-300" : "bg-red-50 border border-red-300"
                    }`}
                  >
                    <div className="text-xs text-gray-500 font-medium flex items-center gap-1">
                      {plPositive ? <TrendingUp className="w-3 h-3 text-green-500" /> : <TrendingDown className="w-3 h-3 text-red-500" />}
                      Trip P&L
                    </div>
                    <div className={`text-lg font-bold ${plPositive ? "text-green-600" : "text-red-600"}`}>
                      {plPositive ? "+" : ""}₹{fmt(contract.trip_pl)}
                    </div>
                  </div>
                </div>

                {/* Payment Summary Strip */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-4">
                  <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                      <IndianRupee className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 font-medium">Contract Value</div>
                      <div className="text-base font-bold text-secondary">₹{fmt(contract.contract_value)}</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                      <Wallet className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 font-medium">Total Paid</div>
                      <div className="text-base font-bold text-green-600">₹{fmt(paidAmount)}</div>
                    </div>
                  </div>
                  <div
                    className={`rounded-xl shadow p-4 flex items-center gap-3 ${
                      parseFloat(balanceAmount) <= 0
                        ? "bg-green-50 border border-green-300"
                        : "bg-red-50 border border-red-300"
                    }`}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                        parseFloat(balanceAmount) <= 0 ? "bg-green-100" : "bg-red-100"
                      }`}
                    >
                      <IndianRupee
                        className={`w-4 h-4 ${parseFloat(balanceAmount) <= 0 ? "text-green-600" : "text-red-600"}`}
                      />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 font-medium">Balance Due</div>
                      <div
                        className={`text-base font-bold ${
                          parseFloat(balanceAmount) <= 0 ? "text-green-600" : "text-red-600"
                        }`}
                      >
                        ₹{fmt(balanceAmount)}
                        {parseFloat(balanceAmount) <= 0 && (
                          <span className="ml-1 text-xs font-medium text-green-500">(Cleared)</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Charges Breakdown */}
                <div className="bg-white rounded-xl shadow p-5 mb-4">
                  <h2 className="font-semibold text-secondary mb-4">Charges Breakdown</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                    {[
                      { label: "Handling Charges", value: contract.handling_charges },
                      { label: "Parking Charges", value: contract.parking_charges },
                      { label: "Other Charges", value: contract.other_charges },
                    ].map((item) => (
                      <div key={item.label} className="flex justify-between items-center border-b border-dashed border-gray-200 pb-2">
                        <span className="text-sm text-gray-600">{item.label}</span>
                        <span className="font-semibold text-sm text-secondary">₹{fmt(item.value)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-200 flex justify-between items-center">
                    <span className="font-semibold text-sm text-gray-600">Contract Value (Trip Cost)</span>
                    <span className="font-bold text-base text-secondary">₹{fmt(contract.contract_value)}</span>
                  </div>
                  <div
                    className={`mt-3 pt-3 border-t-2 flex justify-between items-center ${
                      plPositive ? "border-green-400" : "border-red-400"
                    }`}
                  >
                    <span className="font-bold text-sm text-secondary">Trip P&L</span>
                    <span className={`font-bold text-lg ${plPositive ? "text-green-600" : "text-red-600"}`}>
                      {plPositive ? "+" : ""}₹{fmt(contract.trip_pl)}
                    </span>
                  </div>
                </div>

                {/* ML Sheets */}
                <div className="bg-white rounded-xl shadow overflow-hidden mb-4">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    <h2 className="font-semibold text-secondary">
                      Linked ML Sheets ({contract.ml_sheet_ids?.length || 0})
                    </h2>
                  </div>

                  {!contract.ml_sheet_ids?.length ? (
                    <div className="text-center py-10 text-gray-400">No ML sheets linked</div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {contract.ml_sheet_ids.map((mlId) => {
                        const detail = contract.ml_sheet_details?.find((d) => d.ml_sheet_id === mlId)
                        const isExpanded = expandedML === mlId
                        return (
                          <div key={mlId}>
                            <div
                              className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 cursor-pointer"
                              onClick={() => handleToggleML(mlId, detail)}
                            >
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className="font-bold text-blue-600 text-sm">{mlId}</span>
                                {detail && (
                                  <>
                                    <span className="text-xs text-gray-500">{detail.shipment_ids?.length || 0} shipments</span>
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded border font-semibold ${
                                        detail.status === "OPEN" || detail.status === "open"
                                          ? "border-green-400 bg-green-50 text-green-700"
                                          : "border-gray-300 bg-gray-50 text-gray-700"
                                      }`}
                                    >
                                      {detail.status}
                                    </span>
                                    {mlShipments[mlId] && (
                                      <span className="text-xs font-semibold text-green-700">
                                        Freight ₹{fmt(mlShipments[mlId].reduce((sum, s) => sum + parseFloat(s.freight_charges || 0), 0))}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    navigate(`/ml-sheets/view/${mlId}`)
                                  }}
                                  className="text-blue-600 text-xs font-semibold hover:underline"
                                >
                                  View ML Sheet →
                                </button>
                                <span className="text-gray-400 text-xs">{isExpanded ? "▲" : "▼"}</span>
                              </div>
                            </div>

                            {isExpanded && detail && (
                              <div className="px-5 pb-4 bg-gray-50">
                                {detail.from_locations?.length > 0 && (
                                  <div className="text-xs text-gray-500 mb-2">
                                    Route: {detail.from_locations.map((l) => l.location || l.name).join(", ")} →{" "}
                                    {detail.to_locations?.map((l) => l.location || l.name).join(", ")}
                                    &nbsp;·&nbsp;Vehicle: {detail.vehicle_number}
                                  </div>
                                )}

                                {detail.shipment_ids?.length > 0 ? (
                                  <>
                                    <div className="hidden sm:block overflow-x-auto rounded-xl border border-gray-100 bg-white">
                                      <table className="w-full border-collapse text-sm">
                                        <thead className="bg-gray-50 rounded-t-xl">
                                          <tr className="border-b border-gray-200">
                                            <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">#</th>
                                            <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Shipment ID</th>
                                            <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Sender</th>
                                            <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Receiver</th>
                                            <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Freight</th>
                                            <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                          {mlShipmentsLoading[mlId] ? (
                                            <tr>
                                              <td colSpan={6} className="px-3 py-4 text-center text-xs text-gray-400">
                                                Loading shipments...
                                              </td>
                                            </tr>
                                          ) : (mlShipments[mlId] || detail.shipment_ids).map((s, i) => {
                                            const isObj = typeof s === "object"
                                            const sId = isObj ? (s.shipment_id || s.id) : s
                                            return (
                                              <tr key={sId} className={`hover:bg-gray-50 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                                                <td className="px-3 2xl:px-4 py-3 text-gray-500 align-top">{i + 1}</td>
                                                <td className="px-3 2xl:px-4 py-3 align-top">
                                                  <button
                                                    type="button"
                                                    onClick={() => navigate(`/shipments/view/${encodeURIComponent(sId)}`)}
                                                    className="font-medium text-secondary hover:underline break-words text-left"
                                                  >
                                                    {sId}
                                                  </button>
                                                </td>
                                                <td className="px-3 2xl:px-4 py-3 align-top">
                                                  <div className="text-gray-900 break-words">{isObj ? s.sender_name || "—" : "—"}</div>
                                                  <div className="text-xs text-gray-500 mt-0.5 break-words">
                                                    {isObj ? (s.sender_location || "—") : "—"}
                                                  </div>
                                                </td>
                                                <td className="px-3 2xl:px-4 py-3 align-top">
                                                  <div className="text-gray-900 break-words">{isObj ? s.receiver_name || "—" : "—"}</div>
                                                  <div className="text-xs text-gray-500 mt-0.5 break-words">
                                                    {isObj ? (s.receiver_location || "—") : "—"}
                                                  </div>
                                                </td>
                                                <td className="px-3 2xl:px-4 py-3 align-top whitespace-nowrap text-gray-900">
                                                  {isObj ? `₹${fmt(s.freight_charges || 0)}` : "—"}
                                                </td>
                                                <td className="px-3 2xl:px-4 py-3 align-top uppercase text-gray-900">{isObj ? s.status || "—" : "—"}</td>
                                              </tr>
                                            )
                                          })}
                                        </tbody>
                                      </table>
                                    </div>

                                    <div className="sm:hidden space-y-2 mt-2">
                                      {(mlShipments[mlId] || detail.shipment_ids).map((s, i) => {
                                        const isObj = typeof s === "object"
                                        const sId = isObj ? (s.shipment_id || s.id) : s
                                        return (
                                          <div key={sId} className="bg-white rounded border border-gray-200 p-3">
                                            <div className="flex justify-between items-start gap-2 mb-2">
                                              <div className="min-w-0">
                                                <button
                                                  type="button"
                                                  onClick={() => navigate(`/shipments/view/${encodeURIComponent(sId)}`)}
                                                  className="font-medium text-secondary text-sm hover:underline text-left break-words"
                                                >
                                                  {sId}
                                                </button>
                                                <div className="text-xs text-gray-400">#{i + 1}</div>
                                              </div>
                                              {isObj && s.status ? (
                                                <span className="px-2 py-0.5 rounded text-xs font-semibold bg-gray-100 text-gray-700 whitespace-nowrap uppercase">
                                                  {s.status}
                                                </span>
                                              ) : null}
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                              <div>
                                                <span className="text-gray-500">Sender location:</span>
                                                <p className="font-medium">{isObj ? s.sender_name || '—' : '—'}</p>
                                                <p className="text-gray-600">{isObj ? s.sender_location || '—' : '—'}</p>
                                              </div>
                                              <div>
                                                <span className="text-gray-500">Receiver location:</span>
                                                <p className="font-medium">{isObj ? s.receiver_name || '—' : '—'}</p>
                                                <p className="text-gray-600">{isObj ? s.receiver_location || '—' : '—'}</p>
                                              </div>
                                              <div>
                                                <span className="text-gray-500">Freight:</span>
                                                <p className="font-medium text-gray-900">
                                                  {isObj ? `₹${fmt(s.freight_charges || 0)}` : '—'}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      })}
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-xs text-gray-400 py-4 text-center">No shipments in this ML sheet</p>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>


                {/* ─────────────────── Payment History ─────────────────── */}
                <div className="bg-white rounded-xl shadow overflow-hidden mb-4">
                  {/* Section Header */}
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <IndianRupee className="w-4 h-4 text-blue-500" />
                      <h2 className="font-semibold text-secondary">
                        Payment History ({payments.length})
                      </h2>
                    </div>
                    {canEdit() && (
                      <Button
                        variant="primary"
                        size="sm"
                        icon={Plus}
                        onClick={() => setShowAddPayment(true)}
                      >
                        Add Payment
                      </Button>
                    )}
                  </div>

                  {paymentsLoading ? (
                    <div className="text-center py-8">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-4 border-b-4 border-blue-500"></div>
                    </div>
                  ) : payments.length === 0 ? (
                    <div className="text-center py-10 text-gray-400 text-sm">
                      No payments recorded yet
                      {canEdit() && (
                        <div className="mt-2">
                          <button
                            onClick={() => setShowAddPayment(true)}
                            className="text-blue-500 font-semibold text-xs hover:underline"
                          >
                            + Add first payment
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Desktop table */}
                      <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full border-collapse text-sm">
                          <thead className="bg-gray-50 rounded-t-xl">
                            <tr className="border-b border-gray-200">
                              <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">#</th>
                              <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Payment ID</th>
                              <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Date</th>
                              <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Mode</th>
                              <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Reference</th>
                              <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Amount</th>
                              <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Remarks</th>
                              <th className="px-3 2xl:px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Receipt</th>
                              <th className="px-3 2xl:px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {payments.map((p, i) => (
                              <tr key={p.payment_id} className="hover:bg-gray-50 text-secondary">
                                <td className="px-4 py-2.5 text-gray-400">{i + 1}</td>
                                <td className="px-4 py-2.5 font-bold text-blue-600">{p.payment_id}</td>
                                <td className="px-4 py-2.5">{p.payment_date}</td>
                                <td className="px-4 py-2.5">
                                  {p.payment_mode ? (
                                    <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs font-medium">
                                      {p.payment_mode}
                                    </span>
                                  ) : "—"}
                                </td>
                                <td className="px-4 py-2.5 text-gray-600">{p.reference_number || "—"}</td>
                                <td className="px-4 py-2.5 text-right font-bold text-green-700">
                                  ₹{fmt(p.amount)}
                                </td>
                                <td className="px-4 py-2.5 text-gray-500 max-w-[140px] truncate">
                                  {p.remarks || "—"}
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  {p.has_receipt ? (
                                      <a
                                      href={p.receipt_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-orange-500 underline font-semibold text-xs"
                                    >
                                      View
                                    </a>
                                  ) : (
                                    <span className="text-gray-300 text-xs">—</span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  <RowActionsMenu
                                    isOpen={openDropdown === p.payment_id}
                                    onToggle={() =>
                                      setOpenDropdown(openDropdown === p.payment_id ? null : p.payment_id)
                                    }
                                    onClose={() => setOpenDropdown(null)}
                                    onView={() => setViewPayment(p)}
                                    onEdit={canEdit() ? () => setEditPayment(p) : undefined}
                                    onDelete={
                                      canEdit()
                                        ? () => handleDeletePayment(p)
                                        : undefined
                                    }
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          {/* Total row */}
                          <tfoot>
                            <tr className="bg-gray-50 border-t-2 border-gray-300">
                              <td colSpan={5} className="px-4 py-2.5 font-bold text-sm text-secondary text-right">
                                Total Paid
                              </td>
                              <td className="px-4 py-2.5 text-right font-bold text-sm text-green-700">
                                ₹{fmt(paidAmount)}
                              </td>
                              <td colSpan={3} />
                            </tr>
                            <tr className={`border-t ${parseFloat(balanceAmount) <= 0 ? "bg-green-50" : "bg-red-50"}`}>
                              <td colSpan={5} className="px-4 py-2.5 font-bold text-sm text-secondary text-right">
                                Balance Due
                              </td>
                              <td
                                className={`px-4 py-2.5 text-right font-bold text-sm ${
                                  parseFloat(balanceAmount) <= 0 ? "text-green-700" : "text-red-600"
                                }`}
                              >
                                ₹{fmt(balanceAmount)}
                              </td>
                              <td colSpan={3} />
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Mobile cards */}
                      <div className="sm:hidden divide-y divide-gray-100">
                        {payments.map((p) => (
                          <div key={p.payment_id} className="p-4">
                            <div className="flex justify-between items-start gap-2 mb-3">
                              <div className="min-w-0">
                                <div className="font-bold text-blue-600 text-sm">{p.payment_id}</div>
                                <div className="text-xs text-gray-500">{p.payment_date}</div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {p.payment_mode ? (
                                  <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs font-medium whitespace-nowrap">
                                    {p.payment_mode}
                                  </span>
                                ) : null}
                                <RowActionsMenu
                                  isOpen={openDropdown === `m-${p.payment_id}`}
                                  onToggle={() =>
                                    setOpenDropdown(openDropdown === `m-${p.payment_id}` ? null : `m-${p.payment_id}`)
                                  }
                                  onClose={() => setOpenDropdown(null)}
                                  onView={() => setViewPayment(p)}
                                  onEdit={canEdit() ? () => setEditPayment(p) : undefined}
                                  onDelete={
                                    canEdit()
                                      ? () => handleDeletePayment(p)
                                      : undefined
                                  }
                                  extraItems={
                                    p.has_receipt
                                      ? [{
                                          label: 'View Receipt',
                                          icon: '📄',
                                          onClick: () => window.open(p.receipt_url, '_blank', 'noopener,noreferrer'),
                                        }]
                                      : []
                                  }
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                              <div>
                                <span className="text-gray-500">Amount:</span>
                                <p className="font-bold text-green-700">₹{fmt(p.amount)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Reference:</span>
                                <p className="font-medium text-gray-800">{p.reference_number || '—'}</p>
                              </div>
                              {p.remarks && (
                                <div className="col-span-2">
                                  <span className="text-gray-500">Remarks:</span>
                                  <p className="font-medium text-gray-800">{p.remarks}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {/* Mobile total */}
                        <div className="px-4 py-3 bg-gray-50 flex justify-between text-sm font-bold text-secondary">
                          <span>Total Paid</span>
                          <span className="text-green-700">₹{fmt(paidAmount)}</span>
                        </div>
                        <div
                          className={`px-4 py-3 flex justify-between text-sm font-bold ${
                            parseFloat(balanceAmount) <= 0 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"
                          }`}
                        >
                          <span className="text-secondary">Balance Due</span>
                          <span>₹{fmt(balanceAmount)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {/* ─────────────────── End Payment History ─────────────────── */}

      {/* ── Modals ── */}
      {showAddPayment && (
        <AddPaymentModal
          contractId={contractId}
          balanceAmount={balanceAmount}
          onClose={() => setShowAddPayment(false)}
          onSuccess={handlePaymentSuccess}
        />
      )}
      {editPayment && (
        <EditPaymentModal
          contractId={contractId}
          payment={editPayment}
          balanceAmount={balanceAmount}
          onClose={() => setEditPayment(null)}
          onSuccess={handlePaymentSuccess}
        />
      )}
      {viewPayment && (
        <ViewPaymentModal
          payment={viewPayment}
          onClose={() => setViewPayment(null)}
        />
      )}
    </DashboardLayout>
  )
}

export default ViewOnContract
